import { type ReactNode, createContext, createElement, useContext } from "react";
import salkanLogo from "../assets/partner-logos/salkan.png?inline";
import salkanPortrait from "../assets/section-four/salkan-portrait.png?inline";
import { readHostString } from "./host-props";

export type TLandingTestimonial = {
	authorName: string;
	authorRole: string;
	brandLogoAlt?: string;
	brandLogoSrc?: string;
	href?: string;
	portraitAlt: string;
	portraitSrc: string;
	quote: string;
};

export type TLandingCta = {
	body: string;
	getInTouchLabel: string;
	getStartedLabel: string;
	heading: string;
	partnerLogoCaption: string;
};

/** CMS-agnostic landing copy. Webflow is one source; inject via `LandingCopyProvider` to swap. */
export type TLandingCopy = {
	cta: TLandingCta;
	testimonial: TLandingTestimonial;
};

/** Hardcoded section-four testimonial. */
export const DEFAULT_LANDING_TESTIMONIAL: TLandingTestimonial = {
	authorName: "Tom Dimond",
	authorRole: "Founder  -  SALKAN",
	brandLogoAlt: "SALKAN",
	brandLogoSrc: salkanLogo,
	portraitAlt: "Tom Dimond",
	portraitSrc: salkanPortrait,
	quote:
		"\u201cTriffin helped us grow faster than ever and we\u2019re projecting to nearly triple net profit. Game-changing!\u201d",
};

export const DEFAULT_LANDING_CTA: TLandingCta = {
	body: "Intelligent funding, payments and cash flow management for consumer brands",
	getInTouchLabel: "Get in touch",
	getStartedLabel: "Get started",
	heading: "Growth, without the cash flow headache",
	partnerLogoCaption: "Join the next generation of consumer brands, growing with Triffin",
};

export const DEFAULT_LANDING_COPY: TLandingCopy = {
	cta: DEFAULT_LANDING_CTA,
	testimonial: DEFAULT_LANDING_TESTIMONIAL,
};

export function parseLandingTestimonial(value: unknown): Partial<TLandingTestimonial> | undefined {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return undefined;
	}

	const record = value as Record<string, unknown>;
	const quote = readHostString(record.quote) ?? readHostString(record.testimonialQuote);
	const authorName = readHostString(record.authorName);
	const authorRole = readHostString(record.authorRole);
	const href = readHostString(record.href);
	const portraitAlt = readHostString(record.portraitAlt);
	const portraitSrc = readHostString(record.portraitSrc);
	const brandLogoAlt = readHostString(record.brandLogoAlt);
	const brandLogoSrc = readHostString(record.brandLogoSrc);
	if (!quote && !authorName && !authorRole && !href && !portraitAlt && !portraitSrc && !brandLogoAlt && !brandLogoSrc) {
		return undefined;
	}

	return {
		...(quote ? { quote } : {}),
		...(authorName ? { authorName } : {}),
		...(authorRole ? { authorRole } : {}),
		...(href ? { href } : {}),
		...(portraitAlt ? { portraitAlt } : {}),
		...(portraitSrc ? { portraitSrc } : {}),
		...(brandLogoAlt ? { brandLogoAlt } : {}),
		...(brandLogoSrc ? { brandLogoSrc } : {}),
	};
}

export function parseLandingCta(value: unknown): Partial<TLandingCta> | undefined {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return undefined;
	}

	const record = value as Record<string, unknown>;
	const heading = readHostString(record.heading);
	const body = readHostString(record.body);
	const getInTouchLabel = readHostString(record.getInTouchLabel);
	const getStartedLabel = readHostString(record.getStartedLabel);
	const partnerLogoCaption = readHostString(record.partnerLogoCaption);
	if (!heading && !body && !getInTouchLabel && !getStartedLabel && !partnerLogoCaption) {
		return undefined;
	}

	return {
		...(heading ? { heading } : {}),
		...(body ? { body } : {}),
		...(getInTouchLabel ? { getInTouchLabel } : {}),
		...(getStartedLabel ? { getStartedLabel } : {}),
		...(partnerLogoCaption ? { partnerLogoCaption } : {}),
	};
}

export function mergeLandingTestimonial(overrides?: Partial<TLandingTestimonial>): TLandingTestimonial {
	return {
		authorName: overrides?.authorName ?? DEFAULT_LANDING_TESTIMONIAL.authorName,
		authorRole: overrides?.authorRole ?? DEFAULT_LANDING_TESTIMONIAL.authorRole,
		brandLogoAlt: overrides?.brandLogoAlt ?? DEFAULT_LANDING_TESTIMONIAL.brandLogoAlt,
		brandLogoSrc: overrides?.brandLogoSrc ?? DEFAULT_LANDING_TESTIMONIAL.brandLogoSrc,
		href: overrides?.href ?? DEFAULT_LANDING_TESTIMONIAL.href,
		portraitAlt: overrides?.portraitAlt ?? DEFAULT_LANDING_TESTIMONIAL.portraitAlt,
		portraitSrc: overrides?.portraitSrc ?? DEFAULT_LANDING_TESTIMONIAL.portraitSrc,
		quote: overrides?.quote ?? DEFAULT_LANDING_TESTIMONIAL.quote,
	};
}

export function mergeLandingCta(overrides?: Partial<TLandingCta>): TLandingCta {
	const parsed = parseLandingCta(overrides) ?? {};
	return {
		body: parsed.body ?? DEFAULT_LANDING_CTA.body,
		getInTouchLabel: parsed.getInTouchLabel ?? DEFAULT_LANDING_CTA.getInTouchLabel,
		getStartedLabel: parsed.getStartedLabel ?? DEFAULT_LANDING_CTA.getStartedLabel,
		heading: parsed.heading ?? DEFAULT_LANDING_CTA.heading,
		partnerLogoCaption: parsed.partnerLogoCaption ?? DEFAULT_LANDING_CTA.partnerLogoCaption,
	};
}

export type TLandingCopyOverrides = Partial<TLandingCopy> &
	Partial<TLandingTestimonial> & { testimonialQuote?: string };

export function mergeLandingCopy(overrides?: TLandingCopyOverrides): TLandingCopy {
	const nested = overrides?.testimonial;
	return {
		cta: mergeLandingCta(overrides?.cta),
		testimonial: mergeLandingTestimonial({
			...parseLandingTestimonial(overrides),
			...nested,
		}),
	};
}

const LandingCopyContext = createContext<TLandingCopy | null>(null);

export function LandingCopyProvider({
	copy,
	children,
}: {
	copy?: TLandingCopyOverrides;
	children: ReactNode;
}) {
	const value = mergeLandingCopy(copy);
	return createElement(LandingCopyContext.Provider, { value }, children);
}

/** Prefer a parent CMS provider; otherwise apply the supplied host/adapter snapshot. */
export function LandingCopyBoundary({
	copy,
	children,
}: {
	copy?: TLandingCopyOverrides;
	children: ReactNode;
}) {
	const existing = useContext(LandingCopyContext);
	if (existing) {
		return children;
	}
	return createElement(LandingCopyProvider, { copy, children });
}

export function useLandingCopy(): TLandingCopy {
	return useContext(LandingCopyContext) ?? DEFAULT_LANDING_COPY;
}
